/**
 * Extranet navigation menu — registered as `extranet.sidebarMenu` (« Menu de
 * navigation »): the six entries of the design rendered with the
 * design-system `Sidebar.Section` / `Sidebar.Item` (active state, icon-only
 * tooltips when collapsed, in-app navigation through `useSpaLink`).
 *
 * Configurable-ready:
 * - entries render from the declarative {@link SIDEBAR_MENU_ITEMS} (stable
 *   keys `accueil` / `contrats` / `sinistres` / `primes` / `flotte` /
 *   `pieces`); `_layoutOverrides` item edits (rename / hide / reorder /
 *   tooltip — the `items` bucket, or `fields`, which is the bucket the rule
 *   engine fills for info items) are merged with `applyItemOverrides`;
 * - the title « Menu » is resolved with `resolveCardTitle` but only rendered
 *   when a `setTitle` rule sets one (as the section heading);
 * - the base config (items + title) is published with
 *   `usePublishLayoutConfig` so the admin editor lists every target.
 *
 * Hosting: the entries need a design-system `<Sidebar>` root. The extranet
 * sidebar (desktop + mobile drawer) renders the menu inside one and declares
 * it with {@link SidebarMenuHost}. Anywhere else — the admin preview / config
 * page — the menu hosts itself in its own sidebar on the dark brand surface.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { Icon, Tooltip } from '@components/base/display';
import { Skeleton } from '@components/base/feedback';
import { VStack } from '@components/base/layout';
import { Sidebar } from '@components/base/navigation';
import { ThemeOverride } from '@components/integrated/theme-override';
import {
  applyItemOverrides,
  buildPublishedConfigFromInfoItems,
  resolveCardTitle,
  usePublishLayoutConfig,
  type InfoItemDescriptor,
  type LayoutOverrides,
} from '@components/shared/configurable';

import { extranetPath, isNavItemActive } from '../../navigation';
import { SIDEBAR_TEXT } from '../chromeText';
import { EXTRANET_DARK_SURFACE_THEME } from '../extranetTheme';
import { useSpaLink } from '../useSpaLink';
import {
  SIDEBAR_MENU_ID,
  SIDEBAR_MENU_ITEMS,
  SIDEBAR_MENU_TITLE,
  type SidebarMenuItemDescriptor,
} from './sidebarMenuConfig';

export interface SidebarMenuProps {
  /** Icons only (collapsed docked sidebar): labels move to tooltips. */
  collapsed?: boolean;
  /** Called after an entry is followed (the mobile drawer closes itself). */
  onNavigate?: () => void;
  /** Injected by `withComponentRules` — never passed by hand. */
  _layoutOverrides?: LayoutOverrides;
}

/** `true` inside a `<Sidebar>` root that hosts the menu (see {@link SidebarMenuHost}). */
const SidebarMenuHostContext = createContext(false);

/**
 * Declares that the menu is rendered inside a design-system `<Sidebar>` root
 * (the extranet sidebar and drawer). Without it, the menu renders its own.
 */
export function SidebarMenuHost({ children }: Readonly<{ children: ReactNode }>) {
  return <SidebarMenuHostContext.Provider value>{children}</SidebarMenuHostContext.Provider>;
}

/** Published base config: every entry (items) and the title. */
const PUBLISHED_ITEMS: InfoItemDescriptor[] = SIDEBAR_MENU_ITEMS.map((item) => ({
  key: item.key,
  label: item.label,
  order: item.order,
  ...(item.visible === undefined ? {} : { visible: item.visible }),
  ...(item.tooltip === undefined ? {} : { tooltip: item.tooltip }),
}));
const PUBLISHED_CONFIG = buildPublishedConfigFromInfoItems(PUBLISHED_ITEMS, SIDEBAR_MENU_TITLE);

/** Ascending `order`, declaration order on ties (stable sort). */
function byOrder(a: SidebarMenuItemDescriptor, b: SidebarMenuItemDescriptor): number {
  return a.order - b.order;
}

/**
 * Placeholder of the menu while its rules load (`withComponentRules`
 * render gate): one muted bar per entry, sized like a `Sidebar.Item`.
 */
export function SidebarMenuSkeleton() {
  return (
    <VStack gap={1} className="px-2 py-2" data-testid="extranet-sidebar-menu-loading">
      {SIDEBAR_MENU_ITEMS.map((item) => (
        <Skeleton key={item.key} variant="rectangular" height={40} className="opacity-20" />
      ))}
    </VStack>
  );
}

export function SidebarMenu({
  collapsed = false,
  onNavigate,
  _layoutOverrides,
}: Readonly<SidebarMenuProps>) {
  const hosted = useContext(SidebarMenuHostContext);
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { pathname } = useLocation();
  const spaLink = useSpaLink();

  usePublishLayoutConfig(SIDEBAR_MENU_ID, PUBLISHED_CONFIG);

  const itemOverrides = _layoutOverrides?.items ?? _layoutOverrides?.fields;
  const items = useMemo(
    () =>
      applyItemOverrides(SIDEBAR_MENU_ITEMS, itemOverrides)
        .filter((item) => item.visible !== false)
        .sort(byOrder),
    [itemOverrides],
  );

  // No heading at baseline; a `setTitle` rule adds one above the entries.
  const sectionTitle =
    _layoutOverrides?.title === undefined
      ? undefined
      : resolveCardTitle(SIDEBAR_MENU_TITLE, _layoutOverrides);

  const section = (
    <Sidebar.Section
      title={sectionTitle === '' ? undefined : sectionTitle}
      data-testid="extranet-sidebar-menu"
    >
      {items.map((item) => {
        const href = extranetPath(tenantSlug, item.path);
        const entry = (
          <Sidebar.Item
            key={item.key}
            data-nav-key={item.key}
            // Collapsed, the base item renders the icon only: keep a name.
            aria-label={item.label}
            icon={<Icon icon={item.icon} size="sm" color="inherit" />}
            label={item.label}
            href={href}
            active={isNavItemActive(item, pathname, tenantSlug)}
            onClick={spaLink(href, onNavigate)}
          />
        );
        // Collapsed, the base item already shows its label in a tooltip.
        if (collapsed || item.tooltip === undefined || item.tooltip === '') return entry;
        return (
          <Tooltip key={item.key} content={item.tooltip} position="right">
            {entry}
          </Tooltip>
        );
      })}
    </Sidebar.Section>
  );

  if (hosted) return section;

  // Standalone (admin preview): the same entries in their own dark sidebar.
  return (
    <ThemeOverride config={EXTRANET_DARK_SURFACE_THEME}>
      <Sidebar collapsed={collapsed} aria-label={SIDEBAR_TEXT.ariaLabel}>
        {section}
      </Sidebar>
    </ThemeOverride>
  );
}
